import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@apps/web/components/ui/pagination';

/** Page numbers with ellipsis gaps, e.g. [1, null, 4, 5, 6, null, 12]. */
function paginationPages(
    page: number,
    totalPages: number,
): Array<number | null> {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    const middle = [page - 1, page, page + 1].filter(
        (candidate) => candidate > 1 && candidate < totalPages,
    );
    const pages: Array<number | null> = [1];
    if ((middle[0] ?? totalPages) > 2) {
        pages.push(null);
    }
    pages.push(...middle);
    if ((middle[middle.length - 1] ?? 1) < totalPages - 1) {
        pages.push(null);
    }
    pages.push(totalPages);
    return pages;
}

/** Count + numbered-pagination footer for the notes feed. */
export function NotesPaginationFooter({
    page,
    totalPages,
    total,
    onPageChange,
}: {
    readonly page: number;
    readonly totalPages: number;
    readonly total: number;
    readonly onPageChange: (page: number) => void;
}) {
    return (
        <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span className="tabular-nums">
                {total} notes · page {page} of {Math.max(totalPages, 1)}
            </span>
            <Pagination className="mx-0 w-auto">
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            href="#"
                            aria-disabled={page <= 1}
                            className={
                                page <= 1
                                    ? 'pointer-events-none opacity-50'
                                    : ''
                            }
                            onClick={(event) => {
                                event.preventDefault();
                                onPageChange(Math.max(1, page - 1));
                            }}
                        />
                    </PaginationItem>
                    {paginationPages(page, totalPages).map(
                        (pageNumber, index) =>
                            pageNumber === null ? (
                                <PaginationItem key={`ellipsis-${index}`}>
                                    <PaginationEllipsis />
                                </PaginationItem>
                            ) : (
                                <PaginationItem key={pageNumber}>
                                    <PaginationLink
                                        href="#"
                                        isActive={pageNumber === page}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            onPageChange(pageNumber);
                                        }}
                                    >
                                        {pageNumber}
                                    </PaginationLink>
                                </PaginationItem>
                            ),
                    )}
                    <PaginationItem>
                        <PaginationNext
                            href="#"
                            aria-disabled={page >= totalPages}
                            className={
                                page >= totalPages
                                    ? 'pointer-events-none opacity-50'
                                    : ''
                            }
                            onClick={(event) => {
                                event.preventDefault();
                                onPageChange(Math.min(totalPages, page + 1));
                            }}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
}
